import {
  Component,
  ComponentRef,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  Inject,
  Optional,
  ChangeDetectorRef,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  Renderer2,
  HostBinding,
  OnInit,
  AfterViewInit,
  ContentChild,
  OnDestroy,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  BasePortalOutlet,
  ComponentPortal,
  CdkPortalOutlet,
  TemplatePortal,
} from '@angular/cdk/portal';
import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { GritOverlayConfig } from './grit-overlay-config';
import { AnimationService } from '../core/ui/animation.service';
import { styleVariables } from '../styles/variables';

export function throwGritOverlayContentAlreadyAttachedError() {
  throw Error(
    'Attempting to attach dialog content after content is already attached'
  );
}

@Component({
  selector: 'grit-overlay-container',
  templateUrl: 'grit-overlay-container.html',
  styleUrls: ['grit-overlay.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
})
export class GritOverlayContainer extends BasePortalOutlet implements OnInit {
  /** The portal outlet inside of this container into which the dialog content will be loaded. */
  @ViewChild(CdkPortalOutlet, { static: true }) _portalOutlet: CdkPortalOutlet;

  /** The class that traps and manages focus within the dialog. */
  private _focusTrap: FocusTrap;

  /** Element that was focused before the dialog was opened. Save this to restore upon close. */
  private _elementFocusedBeforeDialogWasOpened: HTMLElement | null = null;
  private _containerHeight;
  private _containerWidth;

  /** State of the dialog animation. */
  _state: 'void' | 'enter' | 'exit';

  /** Emits when an animation state changes. */
  _animationStateChanged = new EventEmitter<any>();

  /** ID of the element that should be considered as the dialog's label. */
  _ariaLabelledBy: string | null;

  /** ID for the container DOM element. */
  _id: string;

  @HostBinding('class.grit-overlay-container') containerClass = true;
  @HostBinding('attr.tabindex') tabindex = -1;
  @HostBinding('attr.aria-modal') ariaModal = true;
  @HostBinding('attr.role') role = this._config.role;
  @HostBinding('id') id = this._id;
  @HostBinding('attr.aria-labelledby') ariaLabelBy = this._config.ariaLabel
    ? null
    : this._ariaLabelledBy;
  @HostBinding('attr.aria-label') ariaLabel = this._config.ariaLabel;
  @HostBinding('attr.aria-describedby') ariaDescribedby =
    this._config.ariaDescribedBy || null;
  @HostBinding('class.modal') modal = false;
  @HostBinding('class.bottom-sheet') bottomSheet = false;
  @HostBinding('class.right-sheet') rightSheet = false;

  constructor(
    private _focusTrapFactory: FocusTrapFactory,
    private _changeDetectorRef: ChangeDetectorRef,
    private _animationService: AnimationService,
    private _renderer: Renderer2,
    @Optional() @Inject(DOCUMENT) private _document: any,
    public _config: GritOverlayConfig,
    public _elementRef: ElementRef
  ) {
    super();
    this._ariaLabelledBy = _config.ariaLabelledBy || null;
  }

  async ngOnInit() {
    this._applyContainerTypeClass();
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'visibility',
      'hidden'
    );
    await this._animationStateChanged.emit('enter');
    await this._startEnterAnimation();
  }

  attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    if (this._portalOutlet.hasAttached()) {
      throwGritOverlayContentAlreadyAttachedError();
    }

    this._savePreviouslyFocusedElement();
    return this._portalOutlet.attachComponentPortal(portal);
  }

  attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
    if (this._portalOutlet.hasAttached()) {
      throwGritOverlayContentAlreadyAttachedError();
    }
    this._savePreviouslyFocusedElement();
    return this._portalOutlet.attachTemplatePortal(portal);
  }

  private _trapFocus() {
    const element = this._elementRef.nativeElement;
    if (!this._focusTrap) {
      this._focusTrap = this._focusTrapFactory.create(element);
    }

    if (this._config.autoFocus) {
      this._focusTrap.focusInitialElementWhenReady();
    } else {
      const activeElement = this._document.activeElement;

      if (activeElement !== element && !element.contains(activeElement)) {
        element.focus();
      }
    }
  }

  private _restoreFocus() {
    const toFocus = this._elementFocusedBeforeDialogWasOpened;

    if (this._config.restoreFocus && toFocus) {
      toFocus.focus();
    }

    if (this._focusTrap) {
      this._focusTrap.destroy();
    }
  }

  private _savePreviouslyFocusedElement() {
    if (this._document) {
      this._elementFocusedBeforeDialogWasOpened = this._document
        .activeElement as HTMLElement;

      if (this._elementRef.nativeElement.focus) {
        Promise.resolve().then(() => this._elementRef.nativeElement.focus());
      }
    }
  }

  /** Callback, invoked whenever an animation on the host completes. */
  _onAnimationDone(state) {
    if (state === 'enter') {
      this._trapFocus();
    } else if (this._state === 'exit') {
      this._restoreFocus();
    }
    this._animationStateChanged.emit(state);
  }

  /** Callback, invoked when an animation on the host starts. */
  async _onAnimationStart(state) {
    if (state === 'enter') {
      await this._startEnterAnimation();
    }

    this._animationStateChanged.emit(state);
  }

  /** Starts the dialog exit animation. */
  async _startExitAnimation(backdrop?: HTMLElement) {
    this._state = 'exit';

    switch (this._config.overlayType) {
      case 'bottomSheet':
        this._containerHeight = await this._elementRef.nativeElement
          .offsetHeight;
        await this._startExitAnimationBottomSheet();
        await this._BackdropExitAnimation(backdrop, 'rgba(33, 43, 54, 0.4)');
        break;
      case 'rightSheet':
        this._containerWidth = await this._elementRef.nativeElement.offsetWidth;
        await this._startExitAnimationRightSheet();
        await this._BackdropExitAnimation(backdrop, 'rgba(33, 43, 54, 0.4)');
        break;
      case 'modal':
        await this._startExitAnimationModal();
        await this._BackdropExitAnimation(backdrop, 'rgba(33, 43, 54, 0.88)');
        break;
    }

    await this._animationStateChanged.emit(this._state);
    this._onAnimationDone(this._state);

    // Mark the container for check so it can react if the
    // view container is using OnPush change detection.
    this._changeDetectorRef.markForCheck();
  }

  /** Starts the dialog exit animation. */
  async _startEnterAnimation() {
    this._state = 'enter';

    switch (this._config.overlayType) {
      case 'bottomSheet':
        this._containerHeight = await this._elementRef.nativeElement
          .offsetHeight;
        await this._preAnimateEnterBottomSheet();
        await this._startEnterAnimationBottomSheet();
        break;
      case 'rightSheet':
        this._containerWidth = await this._elementRef.nativeElement.offsetWidth;
        await this._preAnimateEnterRightSheet();
        await this._startEnterAnimationRightSheet();
        break;
      case 'modal':
        await this._preAnimateEnterModal();
        await this._startEnterAnimationModal();
        break;
    }

    await this._animationStateChanged.emit(this._state);
    this._onAnimationDone(this._state);

    // Mark the container for check so it can react if the
    // view container is using OnPush change detection.
    this._changeDetectorRef.markForCheck();
  }

  private _startEnterAnimationBottomSheet() {
    return this._animationService.animate({
      targets: this._elementRef.nativeElement,
      transform: [`translateY(${this._containerHeight}px)`, `translateY(0)`],
      duration: styleVariables.transitionDurationStandard,
      easing: styleVariables.transitionCurveDeceleration,
      visibility: ['hidden', 'visible'],
      delay: '250ms',
      fillMode: 'forwards',
    });
  }

  private _startExitAnimationBottomSheet() {
    return this._animationService.animate({
      targets: this._elementRef.nativeElement,
      transform: [`translateY(0)`, `translateY(${this._containerHeight}px)`],
      duration: styleVariables.transitionDurationStandard,
      easing: styleVariables.transitionCurveAcceleration,
      fillMode: 'forwards',
    });
  }

  private _preAnimateEnterBottomSheet() {
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'transform',
      `translateY(${this._containerHeight}px)`
    );
  }

  private async _startEnterAnimationRightSheet() {
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'visibility',
      'visible'
    );
    return this._animationService.animate({
      targets: this._elementRef.nativeElement,
      transform: [`translateX(${this._containerWidth}px)`, `translateX(0)`],
      duration: styleVariables.transitionDurationStandard,
      visibility: ['hidden', 'visible'],
      delay: '100ms',
      easing: styleVariables.transitionCurveDeceleration,
      fillMode: 'forwards',
    });
  }

  private _startExitAnimationRightSheet() {
    return this._animationService.animate({
      targets: this._elementRef.nativeElement,
      transform: [`translateX(0)`, `translateX(${this._containerWidth}px)`],
      duration: styleVariables.transitionDurationStandard - 50,
      easing: styleVariables.transitionCurveAcceleration,
      fillMode: 'forwards',
    });
  }

  private _preAnimateEnterRightSheet() {
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'transform',
      `translateX(${this._containerWidth}px)`
    );
  }

  private _startEnterAnimationModal() {
    return this._animationService.animate({
      targets: this._elementRef.nativeElement,
      transform: [`translateY(64px)`, `translateY(0)`],
      opacity: [0, 1],
      duration: styleVariables.transitionDurationStandard - 50,
      visibility: ['hidden', 'visible'],
      delay: '100ms',
      easing: styleVariables.transitionCurveDeceleration,
      fillMode: 'forwards',
    });
  }

  private _startExitAnimationModal() {
    return this._animationService.animate({
      targets: this._elementRef.nativeElement,
      transform: [`translateY(0)`, `translateY(64px)`],
      opacity: [1, 0],
      duration: styleVariables.transitionDurationStandard,
      easing: styleVariables.transitionCurveAcceleration,
      fillMode: 'forwards',
    });
  }

  private _preAnimateEnterModal() {
    this._renderer.setStyle(
      this._elementRef.nativeElement,
      'transform',
      `translateY(64px)`
    );
    this._renderer.setStyle(this._elementRef.nativeElement, 'opacity', `0`);
  }

  private _applyContainerTypeClass() {
    switch (this._config.overlayType) {
      case 'bottomSheet':
        this.bottomSheet = true;
        break;
      case 'rightSheet':
        this.rightSheet = true;
        break;
      case 'modal':
        this.modal = true;
        break;
    }
  }

  async _BackdropExitAnimation(backdrop, startBackgroundColor) {
    return this._animationService.animate({
      targets: backdrop,
      background: [`${startBackgroundColor}`, `rgba(33, 43, 54, 0)`],
      duration: styleVariables.transitionDurationStandard,
      easing: styleVariables.transitionCurveDeceleration,
      fillMode: 'forwards',
    });
  }
}
